
import CategoryUpsertModal from '@/components/categories/CategoryUpsertModal.vue'
import { CategoryNamesPerCombination, UpsertActionType } from '@/components/categories/types'
import ProductSetInfoKeyModal from '@/components/productSetInfoKeys/ProductSetInfoKeyModal.vue'
import ProductSetInfoKeyListModal from '@/components/productSetInfoKeys/ProductSetInfoKeyListModal.vue'
import QUERIES from '@/queries/queries'
import { Component, Vue } from 'vue-property-decorator'
import { DataOptions, DataTableHeader } from 'vuetify'
import {
  CategoriesViewQueryDocument,
  CategoriesViewQueryQuery,
  ProductCategoryFragment,
  ProductCombinationFragment,
  ProductSetInfoKeyTranslationsFragment
} from './gql/__generated__/CategoriesView.query'
import {
  GetCategoryPageDocument,
  GetCategoryPageQuery,
  GetCategoryPageQueryVariables
} from './gql/__generated__/categoryPage.query'
import { InfoKeyToCategories } from './CategoriesViewTypes'

@Component({
  components: {
    CategoryUpsertModal,
    ProductSetInfoKeyModal,
    ProductSetInfoKeyListModal
  }
})
export default class CategoriesView extends Vue {
  categoriesRequestData: ProductCategoryFragment[] = []
  productCombinations: ProductCombinationFragment[] = []
  infoKeyToCategories: InfoKeyToCategories[] = []
  knownProductSetInfoKeys: ProductSetInfoKeyTranslationsFragment[] = []
  tableOptions: DataOptions = {} as DataOptions

  showCategoryUpsertModal = false

  categoryToUpdate: ProductCategoryFragment | null = null
  upsertCategoryActionType: UpsertActionType | null = null

  EMPTY_GUID = '00000000-0000-0000-0000-000000000000'

  get lang() {
    return this.$i18n.locale
  }

  created() {
    this.$apollo.addSmartQuery<CategoriesViewQueryQuery>(QUERIES.CategoriesViewQuery, {
      query: CategoriesViewQueryDocument,
      variables: () => ({ currentLang: this.currentLang }),
      update: (data) => data,
      result: (result) => {
        if (result) {
          this.categoriesRequestData = result.data?.productCategories ?? []
          this.productCombinations = result.data?.productCombinations ?? []
          this.knownProductSetInfoKeys = result.data?.productSetInfoKeys ?? []

          // Build mapping of "OTHER" type info keys to their assigned categories to display in info key list modal. First, clear the array
          // to start with a clean state when this query is re-fetched when the category is updated.
          this.infoKeyToCategories = []
          this.categoriesRequestData.forEach((category) => {
            category.assignedProductCategoryProductSetInfoKeys
              ?.filter((infoKey) => infoKey?.productSetInfoKey?.key === 'OTHER')
              ?.forEach((infoKey) => {
                const infoKeyId = infoKey?.productSetInfoKey?.id
                if (infoKeyId && category.name) {
                  let existingEntry = this.infoKeyToCategories.find((entry) => entry.productSetInfoKeyId === infoKeyId)

                  if (!existingEntry) {
                    existingEntry = {
                      productSetInfoKeyId: infoKeyId,
                      categoryNames: []
                    }
                    this.infoKeyToCategories.push(existingEntry)
                  }

                  existingEntry.categoryNames.push(category.name)
                }
              })
          })

          // If this is triggered from a refetch and we're still inside the upsert category modal, then we need to refresh the category to update with new data
          if (this.categoryToUpdate)
            this.categoryToUpdate = this.categoriesRequestData?.find((x) => x.id === this.categoryToUpdate?.id) ?? null
        }
      },
      error: (error) => {
        this.$store.dispatch('showErrorDialog', {
          Code: 'E4180',
          Message: error
        })
      }
    })
  }

  get isReadonly() {
    return !this.$store.getters.isAdmin
  }

  get headers() {
    return [
      { text: this.$t('application.categories.list.headers.name'), value: 'name', width: '13%' },
      { text: this.$t('application.categories.list.headers.position'), value: 'position', width: '9%' },
      {
        text: this.$t('application.categories.list.headers.productCombination'),
        value: 'productCombination.combinationType',
        width: '13%'
      },
      { text: '', value: 'actions', sortable: false, width: '0%' }
    ] as DataTableHeader[]
  }

  get currentLang() {
    return this.$i18n.locale
  }

  get tableData() {
    return this.categoriesRequestData.map((x) => ({
      id: x?.id,
      name: x?.name,
      position: x?.position,
      productCombination: x?.productCombination
    }))
  }

  navigateToCms(id: string) {
    this.$apollo
      .query<GetCategoryPageQuery, GetCategoryPageQueryVariables>({
        query: GetCategoryPageDocument,
        variables: {
          aliasId: id
        }
      })
      .catch((reason) => {
        this.$store.dispatch('showErrorDialog', {
          Code: 'E1118',
          Message: reason
        })
      })
      .then((response) => {
        if (response) {
          const host = process.env.VUE_APP_CMS_URL
          const contentItemId = response.data.category?.[0]?.contentItemId
          if (contentItemId) {
            const url = `${host}/Admin/Contents/ContentItems/${contentItemId}/Edit?returnUrl=%2FAdmin%2FContents%2FContentItems`
            window.open(url, 'editCategoriesInCms')
          } else {
            this.$store.dispatch('showErrorDialog', {
              Code: 'E1119'
            })
          }
        }
      })
  }

  get categoryNamesPerCombination() {
    const categoryNamesPerCombination = []
    for (let i = 0; i < this.productCombinations.length; i++) {
      const item = {
        combinationId: this.productCombinations[i]?.id,
        categoryNames: this.categoriesRequestData
          ?.filter((x) => x?.productCombination?.id === this.productCombinations[i]?.id)
          .map((y) => y?.name)
      } as CategoryNamesPerCombination
      categoryNamesPerCombination.push(item)
    }
    return categoryNamesPerCombination
  }

  openCategoryUpsertModal(catId: string | null) {
    this.showCategoryUpsertModal = true
    this.categoryToUpdate = this.categoriesRequestData?.find((x) => x.id === catId) ?? null
    this.upsertCategoryActionType = catId ? UpsertActionType.UPDATE : UpsertActionType.SAVE
  }

  openInfoKeyListModal() {
    this.$store.commit('setShowManageCharacteristicsDialog', true)
  }

  closeCategoryUpsertModal() {
    this.showCategoryUpsertModal = false
    this.categoryToUpdate = null
  }
}
